import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import {
  deleteV1MessagingChannelsMessagingChannelIdMessagesId,
  patchV1MessagingChannelsMessagingChannelIdMessagesId,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { replaceMessageInCache } from '~hooks/useMessaging/messagingTranscriptCache';
import { MessagingChannel, MessagingMessage } from '~hooks/useMessaging/models';
import useMessagingChannelListBump from '~hooks/useMessaging/useMessagingChannelListBump';
import { messagingTranscriptQueryKey } from '~queries/messagingQueryKeys';
import type { MessagingMessagesPage } from '~queries/useMessagingMessagesInfiniteQuery';
import connection from '~services/connectionModule';

type DeleteMessageInput = {
  channel: MessagingChannel;
  message: MessagingMessage;
};

type EditMessageInput = DeleteMessageInput & {
  content: string;
};

const editFailureMessage = (error: unknown) =>
  isAxiosError(error) && error.response?.status === 429
    ? t('error_messages.messaging.too_many_edits')
    : t('error_messages.messaging.failed_to_edit_message');

/** Writes into the cache: invalidating would refetch every page scrolled back through. */
export default function useMessageActions() {
  const queryClient = useQueryClient();
  const { replaceChannelPreviewInLists } = useMessagingChannelListBump();

  const replaceInTranscript = useCallback(
    async (channel: MessagingChannel, message: MessagingMessage) => {
      const messagesQueryKey = messagingTranscriptQueryKey(channel.id);
      // Cancel an in-flight fetch so it cannot clobber the write below.
      await queryClient.cancelQueries({ queryKey: messagesQueryKey });
      queryClient.setQueryData<InfiniteData<MessagingMessagesPage>>(
        messagesQueryKey,
        (existing) => replaceMessageInCache(existing, message),
      );
      // The socket echo refreshes the list too, but not while it is disconnected.
      replaceChannelPreviewInLists(channel.id, message);
    },
    [queryClient, replaceChannelPreviewInLists],
  );

  const editMessage = useCallback(
    async ({ channel, content, message }: EditMessageInput) => {
      try {
        const response = await patchV1MessagingChannelsMessagingChannelIdMessagesId({
          path: {
            'messaging-channel-id': channel.id,
            id: message.id,
          },
          body: { content },
        });

        const edited = MessagingMessage.parse(response.data.data);
        await replaceInTranscript(channel, edited);

        return edited;
      } catch (error) {
        connection.handleRequestError(error, editFailureMessage(error));
        throw error;
      }
    },
    [replaceInTranscript],
  );

  const deleteMessage = useCallback(
    async ({ channel, message }: DeleteMessageInput) => {
      try {
        await deleteV1MessagingChannelsMessagingChannelIdMessagesId({
          path: {
            'messaging-channel-id': channel.id,
            id: message.id,
          },
        });

        await replaceInTranscript(channel, message.toTombstone());
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.messaging.failed_to_delete_message'),
        );
        throw error;
      }
    },
    [replaceInTranscript],
  );

  return { deleteMessage, editMessage };
}
